import React from 'react';
import { Component } from 'react';
import './Render3D.css';
import { getImageData, loadImageData, View3D } from 'react-vtkjs-viewport';
import vtkVolume from 'vtk.js/Sources/Rendering/Core/Volume';
import vtkVolumeMapper from 'vtk.js/Sources/Rendering/Core/VolumeMapper';
import vtkColorTransferFunction from 'vtk.js/Sources/Rendering/Core/ColorTransferFunction';
import vtkPiecewiseFunction from 'vtk.js/Sources/Common/DataModel/PiecewiseFunction';
import presets from './presets.js';
import OHIFVTKViewport from './OHIFVTKViewport';
import render3DRef from './render3DRef.js';
import vtkInteractorStyleManipulator from 'vtk.js/Sources/Interaction/Style/InteractorStyleManipulator';
import vtkMouseCameraTrackballRotateManipulator from 'vtk.js/Sources/Interaction/Manipulators/MouseCameraTrackballRotateManipulator';
import vtkMouseCameraTrackballPanManipulator from 'vtk.js/Sources/Interaction/Manipulators/MouseCameraTrackballPanManipulator';
import vtkMouseCameraTrackballZoomManipulator from 'vtk.js/Sources/Interaction/Manipulators/MouseCameraTrackballZoomManipulator';
import vtkAxisLockedRotateManipulator from './AxisLockedRotateManipulator.js';

// Picks, among the volume's 3 orthogonal direction cosines (row/column/
// normal, in DICOM patient LPS space), the one that best aligns with
// `target` (a unit vector), sign-corrected to point the same way as target.
// Used to find "Superior" / "Posterior" regardless of whether the source
// series was acquired axial, coronal, or sagittal.
function pickAxisClosestTo(vectors, target) {
  let best = null;
  let bestAbsDot = -Infinity;

  vectors.forEach(v => {
    const dot = v[0] * target[0] + v[1] * target[1] + v[2] * target[2];
    const absDot = Math.abs(dot);

    if (absDot > bestAbsDot) {
      bestAbsDot = absDot;
      best = dot >= 0 ? v : [-v[0], -v[1], -v[2]];
    }
  });

  return best;
}

function createActorMapper(imageData) {
  const mapper = vtkVolumeMapper.newInstance();
  mapper.setInputData(imageData);

  const actor = vtkVolume.newInstance();
  actor.setMapper(mapper);

  return {
    actor,
    mapper,
  };
}

function getShiftRange(colorTransferArray) {
  // Credit to paraview-glance
  // https://github.com/Kitware/paraview-glance/blob/3fec8eeff31e9c19ad5b6bff8e7159bd745e2ba9/src/components/controls/ColorBy/script.js#L133

  // shift range is original rgb/opacity range centered around 0
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < colorTransferArray.length; i += 4) {
    min = Math.min(min, colorTransferArray[i]);
    max = Math.max(max, colorTransferArray[i]);
  }

  const center = (max - min) / 2;

  return {
    shiftRange: [-center, center],
    min,
    max,
  };
}

function applyPointsToPiecewiseFunction(points, range, pwf) {
  const width = range[1] - range[0];
  const rescaled = points.map(([x, y]) => [x * width + range[0], y]);

  pwf.removeAllPoints();
  rescaled.forEach(([x, y]) => pwf.addPoint(x, y));

  return rescaled;
}

function applyPointsToRGBFunction(points, range, cfun) {
  const width = range[1] - range[0];
  const rescaled = points.map(([x, r, g, b]) => [
    x * width + range[0],
    r,
    g,
    b,
  ]);

  cfun.removeAllPoints();
  rescaled.forEach(([x, r, g, b]) => cfun.addRGBPoint(x, r, g, b));

  return rescaled;
}

function applyPreset(actor, preset) {
  // Create color transfer function
  const colorTransferArray = preset.colorTransfer
    .split(' ')
    .splice(1)
    .map(parseFloat);

  const { shiftRange } = getShiftRange(colorTransferArray);
  let min = shiftRange[0];
  const width = shiftRange[1] - shiftRange[0];
  const cfun = vtkColorTransferFunction.newInstance();
  const normColorTransferValuePoints = [];
  for (let i = 0; i < colorTransferArray.length; i += 4) {
    let value = colorTransferArray[i];
    const r = colorTransferArray[i + 1];
    const g = colorTransferArray[i + 2];
    const b = colorTransferArray[i + 3];

    value = (value - min) / width;
    normColorTransferValuePoints.push([value, r, g, b]);
  }

  applyPointsToRGBFunction(normColorTransferValuePoints, shiftRange, cfun);

  actor.getProperty().setRGBTransferFunction(0, cfun);

  // Create scalar opacity function
  const scalarOpacityArray = preset.scalarOpacity
    .split(' ')
    .splice(1)
    .map(parseFloat);

  const ofun = vtkPiecewiseFunction.newInstance();
  const normPoints = [];
  for (let i = 0; i < scalarOpacityArray.length; i += 2) {
    let value = scalarOpacityArray[i];
    const opacity = scalarOpacityArray[i + 1];

    value = (value - min) / width;

    normPoints.push([value, opacity]);
  }

  applyPointsToPiecewiseFunction(normPoints, shiftRange, ofun);

  actor.getProperty().setScalarOpacity(0, ofun);

  const [
    gradientMinValue,
    gradientMinOpacity,
    gradientMaxValue,
    gradientMaxOpacity,
  ] = preset.gradientOpacity
    .split(' ')
    .splice(1)
    .map(parseFloat);

  actor.getProperty().setUseGradientOpacity(0, true);
  actor.getProperty().setGradientOpacityMinimumValue(0, gradientMinValue);
  actor.getProperty().setGradientOpacityMinimumOpacity(0, gradientMinOpacity);
  actor.getProperty().setGradientOpacityMaximumValue(0, gradientMaxValue);
  actor.getProperty().setGradientOpacityMaximumOpacity(0, gradientMaxOpacity);

  if (preset.interpolation === '1') {
    actor.getProperty().setInterpolationTypeToFastLinear();
    //actor.getProperty().setInterpolationTypeToLinear()
  }

  const ambient = parseFloat(preset.ambient);
  //const shade = preset.shade === '1'
  const diffuse = parseFloat(preset.diffuse);
  const specular = parseFloat(preset.specular);
  const specularPower = parseFloat(preset.specularPower);

  //actor.getProperty().setShade(shade)
  actor.getProperty().setAmbient(ambient);
  actor.getProperty().setDiffuse(diffuse);
  actor.getProperty().setSpecular(specular);
  actor.getProperty().setSpecularPower(specularPower);
}

function createCT3dPipeline(imageData, ctTransferFunctionPresetId) {
  const { actor, mapper } = createActorMapper(imageData);

  const sampleDistance =
    1.2 *
    Math.sqrt(
      imageData
        .getSpacing()
        .map(v => v * v)
        .reduce((a, b) => a + b, 0)
    );

  const range = imageData
    .getPointData()
    .getScalars()
    .getRange();
  actor
    .getProperty()
    .getRGBTransferFunction(0)
    .setRange(range[0], range[1]);

  mapper.setSampleDistance(sampleDistance);

  const preset = presets.find(
    preset => preset.id === ctTransferFunctionPresetId
  );

  applyPreset(actor, preset);

  actor.getProperty().setScalarOpacityUnitDistance(0, 2.5);

  return actor;
}

class Render3D extends Component {
  state = {
    volumeRenderingVolumes: null,
    ctTransferFunctionPresetId: 'vtkMRMLVolumePropertyNode4',
    axisLock: 'free',
    error: null,
  };

  componentWillUnmount() {
    if (render3DRef.current === this) {
      render3DRef.current = null;
    }
  }

  async componentDidMount() {
    render3DRef.current = this;

    const { viewportData } = this.props;
    if (!viewportData) {
      return;
    }

    const { studies, displaySet } = viewportData;
    const { StudyInstanceUID, displaySetInstanceUID, SOPInstanceUID } = displaySet;

    this.apis = [];

    try {
      const stack = OHIFVTKViewport.getCornerstoneStack(
        studies,
        StudyInstanceUID,
        displaySetInstanceUID,
        SOPInstanceUID
      );

      const ctImageDataObject = this.loadDataset(
        stack.imageIds,
        displaySetInstanceUID
      );

      const ctImageData = ctImageDataObject.vtkImageData;
      // Direction cosines from the DICOM orientation (row/column/normal),
      // used to lock rotation to a real anatomical axis instead of the
      // camera's screen-relative one.
      this.imageDirection = ctImageData.getDirection();

      const ctVolVR = createCT3dPipeline(
        ctImageData,
        this.state.ctTransferFunctionPresetId
      );

      this.setState({
        volumeRenderingVolumes: [ctVolVR],
        percentComplete: 0,
      });
    } catch (error) {
      console.error('Render3D: failed to build the volume', error);
      this.setState({ error: error.message || String(error) });
    }
  }

  saveApiReference = api => {
    this.apis = [api];
    this.setInitialCameraView(api);
  };

  // View3D's own resetCamera() picks an arbitrary default direction (often
  // showing the top of the head). Re-orient using the volume's real DICOM
  // patient orientation so it opens facing the patient anteriorly (face
  // toward the viewer), head up — regardless of whether the source series
  // was acquired axial, coronal, or sagittal.
  setInitialCameraView = api => {
    if (!this.imageDirection || !this.state.volumeRenderingVolumes) {
      return;
    }

    const renderer = api.genericRenderWindow.getRenderer();
    const camera = renderer.getActiveCamera();
    const actor = this.state.volumeRenderingVolumes[0];
    const bounds = actor.getBounds();
    const center = [
      (bounds[0] + bounds[1]) / 2,
      (bounds[2] + bounds[3]) / 2,
      (bounds[4] + bounds[5]) / 2,
    ];
    const distance = camera.getDistance();

    const axes = [
      this.imageDirection.slice(0, 3),
      this.imageDirection.slice(3, 6),
      this.imageDirection.slice(6, 9),
    ];
    const superior = pickAxisClosestTo(axes, [0, 0, 1]);
    const posterior = pickAxisClosestTo(axes, [0, 1, 0]);

    // Camera sits on the anterior side (opposite of posterior), looking
    // toward the patient's face, with the head pointing up on screen.
    camera.setPosition(
      center[0] - posterior[0] * distance,
      center[1] - posterior[1] * distance,
      center[2] - posterior[2] * distance
    );
    camera.setFocalPoint(center[0], center[1], center[2]);
    camera.setViewUp(superior[0], superior[1], superior[2]);

    renderer.resetCameraClippingRange();
    api.genericRenderWindow.getRenderWindow().render();
  };

  getAnatomicalAxis = axis => {
    // direction = [rowCosines(x,y,z), columnCosines(x,y,z), scanAxisNormal(x,y,z)]
    const direction = this.imageDirection || [1, 0, 0, 0, 1, 0, 0, 0, 1];
    switch (axis) {
      case 'x':
        return direction.slice(0, 3);
      case 'y':
        return direction.slice(3, 6);
      case 'z':
        return direction.slice(6, 9);
      default:
        return null;
    }
  };

  setAxisLock = axis => {
    const api = this.apis && this.apis[0];
    if (!api || !this.state.volumeRenderingVolumes) {
      return;
    }

    const interactor = api.genericRenderWindow
      .getRenderWindow()
      .getInteractor();
    const lockedAxis = this.getAnatomicalAxis(axis);

    // Rotate around the volume's own center (its bounds midpoint), not the
    // camera's current focal point — otherwise, after any earlier pan, the
    // object appears to orbit around an outside point instead of itself.
    const actor = this.state.volumeRenderingVolumes[0];
    const bounds = actor.getBounds();
    const center = [
      (bounds[0] + bounds[1]) / 2,
      (bounds[2] + bounds[3]) / 2,
      (bounds[4] + bounds[5]) / 2,
    ];

    const istyle = vtkInteractorStyleManipulator.newInstance();

    const rotateManipulator = lockedAxis
      ? vtkAxisLockedRotateManipulator.newInstance({
          button: 1,
          center,
          axis: lockedAxis,
        })
      : vtkMouseCameraTrackballRotateManipulator.newInstance({
          button: 1,
          center,
        });

    istyle.addMouseManipulator(rotateManipulator);
    istyle.addMouseManipulator(
      vtkMouseCameraTrackballPanManipulator.newInstance({ button: 2 })
    );
    istyle.addMouseManipulator(
      vtkMouseCameraTrackballZoomManipulator.newInstance({ button: 3 })
    );

    interactor.setInteractorStyle(istyle);

    this.setState({ axisLock: axis });
  };

  applyPresetById = ctTransferFunctionPresetId => {
    if (!this.state.volumeRenderingVolumes) {
      return;
    }

    const preset = presets.find(
      preset => preset.id === ctTransferFunctionPresetId
    );
    if (!preset) {
      return;
    }

    const actor = this.state.volumeRenderingVolumes[0];

    applyPreset(actor, preset);

    this.rerenderAll();

    this.setState({
      ctTransferFunctionPresetId,
    });
  };

  rerenderAll = () => {
    // Update all render windows, since the automatic re-render might not
    // happen if the viewport is not currently using the painting widget
    Object.keys(this.apis).forEach(viewportIndex => {
      const renderWindow = this.apis[
        viewportIndex
      ].genericRenderWindow.getRenderWindow();

      renderWindow.render();
    });
  };

  loadDataset(imageIds, displaySetInstanceUid) {
    // getImageData() below already caches internally by displaySetInstanceUid
    // and returns the cached object when present, so no external cache to clear.
    const imageDataObject = getImageData(imageIds, displaySetInstanceUid);
    loadImageData(imageDataObject);
    const numberOfFrames = imageIds.length;

    const onPixelDataInsertedCallback = numberProcessed => {
      const percentComplete = Math.floor(
        (numberProcessed * 100) / numberOfFrames
      );

      if (this.state.percentComplete !== percentComplete) {
        this.setState({ percentComplete });
      }

      if (percentComplete % 20 === 0) {
        this.rerenderAll();
      }
    };

    const onAllPixelDataInsertedCallback = () => {
      // getImageData() (react-vtkjs-viewport) calls this callback and only
      // *afterwards* marks the underlying vtkImageData as modified, so a
      // render triggered synchronously here still paints the stale/zeroed
      // volume. Defer to the next tick so it runs after that modified() call.
      setTimeout(() => this.rerenderAll(), 0);
    };

    imageDataObject.onPixelDataInserted(onPixelDataInsertedCallback);
    imageDataObject.onAllPixelDataInserted(onAllPixelDataInsertedCallback);

    return imageDataObject;
  }

  render() {
    if (this.state.error) {
      return (
        <h4 className="loading">
          Não foi possível gerar a renderização 3D: {this.state.error}
        </h4>
      );
    }

    if (!this.state.volumeRenderingVolumes) {
      return <h4 className="loading">Carregando...</h4>;
    }

    return (
      <>
        <div className="render">
          <View3D
            volumes={this.state.volumeRenderingVolumes}
            onCreated={this.saveApiReference}
          />
        </div>
      </>
    );
  }
}

export default Render3D;
